import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Calendar, Clock, TrendingUp, Play, Pause } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const ScheduleAnalytics = ({ workflowId, isEnabled, onToggle }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workflowId) {
      fetchStats();
      // Refresh every 30 seconds
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    }
  }, [workflowId]);

  const fetchStats = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_URL}/api/workflows/${workflowId}/schedule/analytics`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch schedule analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.floor(Math.abs(diffMs) / 60000);
    const diffHours = Math.floor(Math.abs(diffMs) / 3600000);
    const diffDays = Math.floor(Math.abs(diffMs) / 86400000);

    if (diffMs > 0) {
      // Future
      if (diffMins < 60) return `in ${diffMins}m`;
      if (diffHours < 24) return `in ${diffHours}h`;
      return `in ${diffDays}d`;
    } else {
      // Past
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    }
  };

  if (loading) {
    return <div className="animate-pulse p-4">Loading schedule analytics...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="w-5 h-5" />
            Schedule Analytics
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="schedule-active" className="text-sm cursor-pointer">
              {isEnabled ? 'Active' : 'Inactive'}
            </Label>
            <Switch
              id="schedule-active"
              checked={isEnabled}
              onCheckedChange={onToggle}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Badge */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <span className="text-sm font-medium">Status</span>
          <Badge variant={isEnabled ? 'default' : 'secondary'} className="flex items-center gap-1">
            {isEnabled ? (
              <><Play className="w-3 h-3" /> Enabled</>
            ) : (
              <><Pause className="w-3 h-3" /> Disabled</>
            )}
          </Badge>
        </div>

        {stats && (
          <>
            {/* Next Run */}
            {stats.next_run && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-green-600" />
                  <p className="text-sm font-semibold text-green-900">Next Scheduled Run</p>
                </div>
                <p className="text-sm text-green-700">
                  {formatDate(stats.next_run)}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {formatRelativeTime(stats.next_run)}
                </p>
              </div>
            )}

            {/* Last Run */}
            {stats.last_run && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <p className="text-sm font-semibold text-blue-900">Last Run</p>
                </div>
                <p className="text-sm text-blue-700">
                  {formatDate(stats.last_run)}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {formatRelativeTime(stats.last_run)}
                </p>
              </div>
            )}

            {/* Execution Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Total Runs</p>
                <p className="text-2xl font-bold text-purple-600">
                  {stats.total_runs || 0}
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-xs text-muted-foreground">Success Rate</p>
                <div className="flex items-center gap-1">
                  <p className="text-2xl font-bold text-green-600">
                    {stats.success_rate || 0}%
                  </p>
                  {stats.success_rate >= 90 && (
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  )}
                </div>
              </div>
            </div>

            {/* Recent Executions */}
            {stats.recent_executions && stats.recent_executions.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold">Recent Executions</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {stats.recent_executions.map((exec, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 bg-muted rounded text-xs"
                    >
                      <span>{formatDate(exec.executed_at)}</span>
                      <Badge
                        variant={exec.status === 'completed' ? 'success' : 'destructive'}
                        className="text-xs"
                      >
                        {exec.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Info Note */}
        <div className="text-xs text-muted-foreground bg-blue-50 p-3 rounded-lg">
          <p>
            {isEnabled 
              ? '✅ This workflow will run automatically based on the configured schedule.'
              : '⏸️ Schedule is disabled. Enable to activate automatic execution.'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default ScheduleAnalytics;
